import { PAYWALL_PLACEMENTS } from '../../src/lib/paywall-placement';

export const REPORT_SCHEMA_VERSION = 1;
export const MAX_REPORT_BYTES = 256 * 1024;
export const LIFETIME_PRODUCT_STORE_IDENTIFIER = 'vaava_plus_lifetime';
export const AFTER_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

const DAY_MS = 24 * 60 * 60 * 1000;
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

export type Platform = 'ios' | 'android';
export type PurchaseLookup = 'complete' | 'not_found' | 'failed' | 'unrecognized_product';

export type TrialInput = {
  billingIdentity: string;
  source: string;
  placement?: string | null;
  storeTransactionId: string | null;
  startedAt: number;
  expiresAt: number;
  originalPurchasedAt: number | null;
  lastGrantOk: number | null;
  firstGrantedAt?: number | null;
};

export type PurchaseInput = {
  billingIdentity: string;
  purchasedAt: number | null;
  status: 'owned' | 'refunded';
  environment: 'production' | 'sandbox';
  store: string;
  storePurchaseIdentifier: string;
  ownership: string;
  productStoreIdentifier: string;
  transferred: boolean;
};

export type IdentityEvidence = {
  billingIdentity: string;
  purchaseLookup: PurchaseLookup;
};

export type CollectionMeta = {
  startedAt: string | null;
  finishedAt: string | null;
  complete: boolean;
  stopReason: string | null;
  sources: {
    trials: 'offline' | 'd1';
    purchases: 'offline' | 'revenuecat_v2';
  };
};

export type ReportRequest = {
  cohortStart: string;
  cohortEnd: string;
  observationCutoff: string;
  generatedAt: string;
  collection: CollectionMeta;
  trials: TrialInput[];
  purchases: PurchaseInput[];
  evidence: IdentityEvidence[];
  clientLifetimeAssertions: number;
};

export type CohortRow = {
  weekStart: string;
  platform: Platform;
  confirmedTrials: number;
  immatureTrials: number;
  duringTrialPurchases: number;
  afterTrialPurchases: number;
  refunds: number;
  unknown: number;
  matureTrials: number;
  converted: number;
};

export type TrialConversionReport = {
  schemaVersion: typeof REPORT_SCHEMA_VERSION;
  generatedAt: string;
  observationCutoff: string;
  cohortStart: string;
  cohortEnd: string;
  collection: CollectionMeta & { provisional: boolean };
  coverage: {
    confirmedTrials: number;
    immatureTrials: number;
    matureTrials: number;
    converted: number;
    unknownRecords: number;
    missingPurchaseEvidence: number;
    excludedExistingLifetime: number;
    duplicateTrialsCollapsed: number;
    refundedPurchases: number;
    clientLifetimeAssertions: number;
  };
  headline: {
    matureTrials: number;
    converted: number;
    rate: number | null;
  };
  cohorts: CohortRow[];
  trialStartsByPlacement: Array<{ platform: Platform; placement: string; trialStarts: number }>;
  limitations: string[];
};

type CohortAccumulator = CohortRow;

function parseUtcDay(value: string): number | null {
  const match = DATE_ONLY.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!year || !month || !day) return null;
  const ms = Date.UTC(year, month - 1, day);
  const check = new Date(ms);
  if (
    check.getUTCFullYear() !== year ||
    check.getUTCMonth() !== month - 1 ||
    check.getUTCDate() !== day
  ) {
    return null;
  }
  return ms;
}

function parseIso(value: string): number | null {
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function utcWeekStart(ms: number): string {
  const date = new Date(ms);
  const weekday = date.getUTCDay();
  const mondayOffset = weekday === 0 ? 6 : weekday - 1;
  const start = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()) - mondayOffset * DAY_MS;
  return new Date(start).toISOString().slice(0, 10);
}

function platformFor(source: string): Platform | null {
  if (source === 'apple_iap') return 'ios';
  if (source === 'android') return 'android';
  return null;
}

export function trialGrantConfirmed(trial: TrialInput): boolean {
  const granted = trial.firstGrantedAt != null || trial.lastGrantOk === 1;
  if (trial.source === 'apple_iap') {
    return trial.storeTransactionId != null && trial.storeTransactionId.length > 0 && granted;
  }
  if (trial.source === 'android') {
    return granted;
  }
  return false;
}

function trialStartMs(trial: TrialInput): number {
  return trial.originalPurchasedAt ?? trial.startedAt;
}

function isLifetimePurchase(purchase: PurchaseInput): boolean {
  return (
    purchase.productStoreIdentifier === LIFETIME_PRODUCT_STORE_IDENTIFIER &&
    purchase.environment === 'production' &&
    (purchase.store === 'app_store' || purchase.store === 'play_store')
  );
}

function purchaseKey(purchase: PurchaseInput): string {
  return `${purchase.store}|${purchase.environment}|${purchase.storePurchaseIdentifier}`;
}

function dedupePurchases(purchases: PurchaseInput[]): PurchaseInput[] {
  const byKey = new Map<string, PurchaseInput>();
  for (const purchase of purchases) {
    const key = purchaseKey(purchase);
    const existing = byKey.get(key);
    if (!existing || (existing.status !== 'refunded' && purchase.status === 'refunded')) {
      byKey.set(key, purchase);
    }
  }
  return [...byKey.values()];
}

function emptyCohort(weekStart: string, platform: Platform): CohortAccumulator {
  return {
    weekStart,
    platform,
    confirmedTrials: 0,
    immatureTrials: 0,
    duringTrialPurchases: 0,
    afterTrialPurchases: 0,
    refunds: 0,
    unknown: 0,
    matureTrials: 0,
    converted: 0,
  };
}

function cohortKey(weekStart: string, platform: Platform): string {
  return `${weekStart}|${platform}`;
}

const STANDING_LIMITATIONS = [
  'Counts are billing identities, not people or households.',
  'Trial rows do not record environment or app version, so sandbox trials stored in plus_trials stay in the extract.',
  'Refund status is as of collection. Purchase timing stops at the observation cutoff.',
  'This report does not explain feature use, return visits, or why a trial did not purchase.',
];

export function buildTrialConversionReport(request: ReportRequest): TrialConversionReport {
  const cohortStart = parseUtcDay(request.cohortStart);
  const cohortEnd = parseUtcDay(request.cohortEnd);
  const cutoff = parseIso(request.observationCutoff);
  if (cohortStart == null || cohortEnd == null || cutoff == null || cohortStart >= cohortEnd) {
    throw new Error('cohort dates or observation cutoff are invalid');
  }

  const evidenceByIdentity = new Map<string, PurchaseLookup>();
  for (const entry of request.evidence) {
    evidenceByIdentity.set(entry.billingIdentity, entry.purchaseLookup);
  }
  const purchasesByIdentity = new Map<string, PurchaseInput[]>();
  for (const purchase of dedupePurchases(request.purchases)) {
    const list = purchasesByIdentity.get(purchase.billingIdentity) ?? [];
    list.push(purchase);
    purchasesByIdentity.set(purchase.billingIdentity, list);
  }

  let duplicateTrialsCollapsed = 0;
  let excludedExistingLifetime = 0;
  let missingPurchaseEvidence = 0;
  let unscopedUnknown = 0;

  const cohorts = new Map<string, CohortAccumulator>();
  const trialStartsByPlacement = new Map<string, { platform: Platform; placement: string; trialStarts: number }>();
  const ensure = (weekStart: string, platform: Platform): CohortAccumulator => {
    const key = cohortKey(weekStart, platform);
    const existing = cohorts.get(key);
    if (existing) return existing;
    const created = emptyCohort(weekStart, platform);
    cohorts.set(key, created);
    return created;
  };

  const ordered = [...request.trials].sort((left, right) => trialStartMs(left) - trialStartMs(right));
  const inCohort = ordered.filter((trial) => {
    const start = trialStartMs(trial);
    return start >= cohortStart && start < cohortEnd;
  });
  const byIdentity = new Map<string, TrialInput>();
  for (const trial of inCohort) {
    if (byIdentity.has(trial.billingIdentity)) {
      duplicateTrialsCollapsed += 1;
      continue;
    }
    byIdentity.set(trial.billingIdentity, trial);
  }
  const byTransaction = new Map<string, TrialInput>();
  const kept: TrialInput[] = [];
  for (const trial of byIdentity.values()) {
    const transactionId = trial.storeTransactionId;
    if (!transactionId) {
      kept.push(trial);
      continue;
    }
    const existing = byTransaction.get(transactionId);
    if (!existing) {
      byTransaction.set(transactionId, trial);
      kept.push(trial);
      continue;
    }
    duplicateTrialsCollapsed += 1;
    if (!trialGrantConfirmed(existing) && trialGrantConfirmed(trial)) {
      const index = kept.indexOf(existing);
      if (index >= 0) kept[index] = trial;
      byTransaction.set(transactionId, trial);
    }
  }

  for (const trial of kept) {
    const start = trialStartMs(trial);
    const platform = platformFor(trial.source);
    if (!platform) {
      unscopedUnknown += 1;
      continue;
    }
    const row = ensure(utcWeekStart(start), platform);
    if (!trialGrantConfirmed(trial)) {
      row.unknown += 1;
      continue;
    }

    // Count confirmed starts even when lifetime purchase evidence is incomplete.
    const placement = PAYWALL_PLACEMENTS.includes(trial.placement as typeof PAYWALL_PLACEMENTS[number])
      ? trial.placement! : 'unknown';
    const placementKey = `${platform}:${placement}`;
    const starts = trialStartsByPlacement.get(placementKey) ?? { platform, placement, trialStarts: 0 };
    starts.trialStarts += 1;
    trialStartsByPlacement.set(placementKey, starts);

    const lookup = evidenceByIdentity.get(trial.billingIdentity) ?? 'failed';
    if (lookup !== 'complete') {
      row.unknown += 1;
      missingPurchaseEvidence += 1;
      continue;
    }

    const candidate = classifyPurchases(
      purchasesByIdentity.get(trial.billingIdentity) ?? [],
      start,
      trial.expiresAt,
      cutoff,
    );
    if (candidate.kind === 'excluded_existing') {
      excludedExistingLifetime += 1;
      continue;
    }
    if (candidate.kind === 'unknown') {
      row.unknown += 1;
      missingPurchaseEvidence += 1;
      continue;
    }

    row.confirmedTrials += 1;
    const mature = trial.expiresAt + AFTER_WINDOW_MS <= cutoff;
    if (!mature) row.immatureTrials += 1;
    else row.matureTrials += 1;
    if (candidate.kind === 'during') {
      row.duringTrialPurchases += 1;
      if (candidate.refunded) row.refunds += 1;
      if (mature) row.converted += 1;
    } else if (candidate.kind === 'after') {
      row.afterTrialPurchases += 1;
      if (candidate.refunded) row.refunds += 1;
      if (mature) row.converted += 1;
    }
  }

  const cohortRows = [...cohorts.values()].sort((left, right) => {
    if (left.weekStart !== right.weekStart) return left.weekStart < right.weekStart ? -1 : 1;
    return left.platform < right.platform ? -1 : 1;
  });
  const coverage = cohortRows.reduce(
    (total, row) => ({
      confirmedTrials: total.confirmedTrials + row.confirmedTrials,
      immatureTrials: total.immatureTrials + row.immatureTrials,
      matureTrials: total.matureTrials + row.matureTrials,
      converted: total.converted + row.converted,
      unknownRecords: total.unknownRecords + row.unknown,
      refundedPurchases: total.refundedPurchases + row.refunds,
    }),
    {
      confirmedTrials: 0,
      immatureTrials: 0,
      matureTrials: 0,
      converted: 0,
      unknownRecords: 0,
      refundedPurchases: 0,
    },
  );
  coverage.unknownRecords += unscopedUnknown;
  const complete = request.collection.complete && missingPurchaseEvidence === 0;
  const showRate = complete && coverage.matureTrials > 0;
  const limitations = [...STANDING_LIMITATIONS];
  if (request.clientLifetimeAssertions > 0) {
    limitations.push(
      `${request.clientLifetimeAssertions} client lifetime assertions were ignored as purchase evidence.`,
    );
  }
  if (!complete) {
    limitations.push('Collection is incomplete, so this report has no conversion rate.');
  }

  return {
    schemaVersion: REPORT_SCHEMA_VERSION,
    generatedAt: request.generatedAt,
    observationCutoff: request.observationCutoff,
    cohortStart: request.cohortStart,
    cohortEnd: request.cohortEnd,
    collection: {
      ...request.collection,
      complete,
      provisional: coverage.immatureTrials > 0,
    },
    coverage: {
      ...coverage,
      missingPurchaseEvidence,
      excludedExistingLifetime,
      duplicateTrialsCollapsed,
      clientLifetimeAssertions: request.clientLifetimeAssertions,
    },
    headline: {
      matureTrials: coverage.matureTrials,
      converted: coverage.converted,
      rate: showRate ? coverage.converted / coverage.matureTrials : null,
    },
    cohorts: cohortRows,
    trialStartsByPlacement: [...trialStartsByPlacement.values()].sort((a, b) =>
      `${a.platform}:${a.placement}`.localeCompare(`${b.platform}:${b.placement}`)),
    limitations,
  };
}

type ClassifiedPurchase =
  | { kind: 'none' }
  | { kind: 'during'; refunded: boolean }
  | { kind: 'after'; refunded: boolean }
  | { kind: 'excluded_existing' }
  | { kind: 'unknown' };

function classifyPurchases(
  purchases: PurchaseInput[],
  trialStart: number,
  trialExpiry: number,
  cutoff: number,
): ClassifiedPurchase {
  const lifetime = purchases.filter(isLifetimePurchase);
  const ambiguous = lifetime.filter((purchase) => purchase.ownership !== 'purchased' || purchase.transferred);
  const clean = lifetime.filter((purchase) => purchase.ownership === 'purchased' && !purchase.transferred);
  if (clean.length === 0 && ambiguous.length > 0) return { kind: 'unknown' };
  if (clean.some((purchase) => purchase.purchasedAt == null)) return { kind: 'unknown' };

  const timed = clean
    .filter((purchase): purchase is PurchaseInput & { purchasedAt: number } => purchase.purchasedAt != null)
    .filter((purchase) => purchase.purchasedAt <= cutoff)
    .sort((left, right) => left.purchasedAt - right.purchasedAt);
  const priorOwned = timed.find((purchase) => purchase.purchasedAt < trialStart && purchase.status === 'owned');
  if (priorOwned) return { kind: 'excluded_existing' };

  const inView = timed.filter((purchase) => purchase.purchasedAt >= trialStart);
  const first = inView[0];
  if (!first) return { kind: 'none' };
  if (first.purchasedAt < trialExpiry) return { kind: 'during', refunded: first.status === 'refunded' };
  if (first.purchasedAt < trialExpiry + AFTER_WINDOW_MS) {
    return { kind: 'after', refunded: first.status === 'refunded' };
  }
  return { kind: 'none' };
}

export function reportStatus(report: TrialConversionReport): 'incomplete' | 'provisional' | 'complete' {
  if (!report.collection.complete) return 'incomplete';
  if (report.collection.provisional) return 'provisional';
  return 'complete';
}

export function canShowConversionRate(report: TrialConversionReport): boolean {
  return (
    report.collection.complete &&
    report.coverage.missingPurchaseEvidence === 0 &&
    report.headline.matureTrials > 0 &&
    report.headline.rate != null
  );
}

export function formatRate(rate: number | null, show: boolean): string {
  if (!show || rate == null) return '—';
  return `${(rate * 100).toFixed(1)}%`;
}

export async function readLocalReportFile(file: {
  text: () => Promise<string>;
  size: number;
}): Promise<{ ok: true; report: TrialConversionReport } | { ok: false; error: string }> {
  const text = await file.text();
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { ok: false, error: 'Report is not valid JSON.' };
  }
  return parseTrialConversionReport(parsed, file.size);
}

export function formatTrialConversionMarkdown(report: TrialConversionReport): string {
  const show = canShowConversionRate(report);
  const lines = [
    '# Trial conversion',
    '',
    `Generated: ${report.generatedAt}`,
    `Data through: ${report.observationCutoff}`,
    `Cohort: ${report.cohortStart} inclusive to ${report.cohortEnd} exclusive`,
    `Status: ${reportStatus(report)}`,
    '',
    `Mature trials: ${report.headline.matureTrials}`,
    `Converted: ${show ? report.headline.converted : '—'}`,
    `Rate: ${formatRate(report.headline.rate, show)}`,
    '',
    '| Cohort | Platform | Trials | During | After | Refunds | Unknown |',
    '| --- | --- | ---: | ---: | ---: | ---: | ---: |',
    ...report.cohorts.map(
      (row) =>
        `| ${row.weekStart} | ${row.platform} | ${row.confirmedTrials} | ${row.duringTrialPurchases} | ${row.afterTrialPurchases} | ${row.refunds} | ${row.unknown} |`,
    ),
    '',
    '| Trial start location | Platform | Confirmed starts |',
    '| --- | --- | ---: |',
    ...report.trialStartsByPlacement.map((row) =>
      `| ${row.placement} | ${row.platform} | ${row.trialStarts} |`),
    '',
    ...report.limitations.map((line) => `- ${line}`),
    '',
  ];
  return lines.join('\n');
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value != null && !Array.isArray(value);
}

function safeCount(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null;
}

function plainString(value: unknown, max: number): string | null {
  if (typeof value !== 'string' || value.length === 0 || value.length > max) return null;
  if (value.includes('<') || value.includes('>')) return null;
  return value;
}

export function parseTrialConversionReport(
  value: unknown,
  byteLength: number,
): { ok: true; report: TrialConversionReport } | { ok: false; error: string } {
  if (byteLength > MAX_REPORT_BYTES) return { ok: false, error: 'Report file is too large.' };
  if (!isRecord(value)) return { ok: false, error: 'Report must be a JSON object.' };
  if (value.schemaVersion !== REPORT_SCHEMA_VERSION) return { ok: false, error: 'Unsupported report version.' };
  const generatedAt = plainString(value.generatedAt, 40);
  const observationCutoff = plainString(value.observationCutoff, 40);
  const cohortStart = plainString(value.cohortStart, 10);
  const cohortEnd = plainString(value.cohortEnd, 10);
  if (!generatedAt || !observationCutoff || !cohortStart || !cohortEnd) {
    return { ok: false, error: 'Report dates are missing.' };
  }
  if (parseIso(generatedAt) == null || parseIso(observationCutoff) == null) {
    return { ok: false, error: 'Report timestamps are invalid.' };
  }
  if (parseUtcDay(cohortStart) == null || parseUtcDay(cohortEnd) == null) {
    return { ok: false, error: 'Cohort dates are invalid.' };
  }
  if (!isRecord(value.collection) || !isRecord(value.coverage) || !isRecord(value.headline)) {
    return { ok: false, error: 'Report sections are missing.' };
  }
  if (!Array.isArray(value.cohorts) || value.cohorts.length > 500) {
    return { ok: false, error: 'Cohort table is missing or too large.' };
  }
  if (!Array.isArray(value.limitations) || value.limitations.length > 20) {
    return { ok: false, error: 'Report limitations are invalid.' };
  }
  const complete = value.collection.complete;
  const provisional = value.collection.provisional;
  if (typeof complete !== 'boolean' || typeof provisional !== 'boolean') {
    return { ok: false, error: 'Collection status is invalid.' };
  }
  const sources = value.collection.sources;
  if (!isRecord(sources) || (sources.trials !== 'offline' && sources.trials !== 'd1')) {
    return { ok: false, error: 'Trial source is invalid.' };
  }
  if (sources.purchases !== 'offline' && sources.purchases !== 'revenuecat_v2') {
    return { ok: false, error: 'Purchase source is invalid.' };
  }
  const startedAt = value.collection.startedAt;
  const finishedAt = value.collection.finishedAt;
  const stopReason = value.collection.stopReason;
  if (!(startedAt == null || typeof startedAt === 'string')) return { ok: false, error: 'Collection start is invalid.' };
  if (!(finishedAt == null || typeof finishedAt === 'string')) {
    return { ok: false, error: 'Collection finish is invalid.' };
  }
  if (!(stopReason == null || typeof stopReason === 'string')) return { ok: false, error: 'Stop reason is invalid.' };
  if (typeof stopReason === 'string' && (stopReason.length > 80 || stopReason.includes('<') || stopReason.includes('>'))) {
    return { ok: false, error: 'Stop reason is invalid.' };
  }

  const coverageFields = [
    'confirmedTrials',
    'immatureTrials',
    'matureTrials',
    'converted',
    'unknownRecords',
    'missingPurchaseEvidence',
    'excludedExistingLifetime',
    'duplicateTrialsCollapsed',
    'refundedPurchases',
    'clientLifetimeAssertions',
  ] as const;
  const coverage = {} as TrialConversionReport['coverage'];
  for (const field of coverageFields) {
    const count = safeCount(value.coverage[field]);
    if (count == null) return { ok: false, error: 'Coverage counts are invalid.' };
    coverage[field] = count;
  }
  const matureTrials = safeCount(value.headline.matureTrials);
  const converted = safeCount(value.headline.converted);
  const parsedRate = value.headline.rate;
  if (matureTrials == null || converted == null) return { ok: false, error: 'Headline counts are invalid.' };
  if (!(parsedRate == null || (typeof parsedRate === 'number' && Number.isFinite(parsedRate) && parsedRate >= 0 && parsedRate <= 1))) {
    return { ok: false, error: 'Conversion rate is invalid.' };
  }
  const rate = typeof parsedRate === 'number' ? parsedRate : null;
  const cohorts: CohortRow[] = [];
  for (const entry of value.cohorts) {
    if (!isRecord(entry)) return { ok: false, error: 'A cohort row is invalid.' };
    const weekStart = plainString(entry.weekStart, 10);
    if (!weekStart || parseUtcDay(weekStart) == null) return { ok: false, error: 'A cohort week is invalid.' };
    if (entry.platform !== 'ios' && entry.platform !== 'android') {
      return { ok: false, error: 'A cohort platform is invalid.' };
    }
    const counts = [
      'confirmedTrials',
      'immatureTrials',
      'duringTrialPurchases',
      'afterTrialPurchases',
      'refunds',
      'unknown',
      'matureTrials',
      'converted',
    ] as const;
    const row = { weekStart, platform: entry.platform } as CohortRow;
    for (const field of counts) {
      const count = safeCount(entry[field]);
      if (count == null) return { ok: false, error: 'A cohort count is invalid.' };
      row[field] = count;
    }
    cohorts.push(row);
  }
  const trialStartsByPlacement: TrialConversionReport['trialStartsByPlacement'] = [];
  // Reports exported before placement tracking are still readable.
  if (value.trialStartsByPlacement !== undefined) {
    if (!Array.isArray(value.trialStartsByPlacement) || value.trialStartsByPlacement.length > 14) {
      return { ok: false, error: 'Trial start locations are invalid.' };
    }
    const seen = new Set<string>();
    for (const entry of value.trialStartsByPlacement) {
      if (!isRecord(entry) || (entry.platform !== 'ios' && entry.platform !== 'android') ||
          (entry.placement !== 'unknown' && !PAYWALL_PLACEMENTS.includes(entry.placement as typeof PAYWALL_PLACEMENTS[number]))) {
        return { ok: false, error: 'A trial start location is invalid.' };
      }
      const trialStarts = safeCount(entry.trialStarts);
      const key = `${entry.platform}:${entry.placement}`;
      if (trialStarts == null || seen.has(key)) return { ok: false, error: 'Trial start counts are invalid.' };
      seen.add(key);
      trialStartsByPlacement.push({ platform: entry.platform, placement: entry.placement as string, trialStarts });
    }
  }
  const limitations: string[] = [];
  for (const line of value.limitations) {
    const text = plainString(line, 400);
    if (!text) return { ok: false, error: 'A limitation line is invalid.' };
    limitations.push(text);
  }

  const report: TrialConversionReport = {
    schemaVersion: REPORT_SCHEMA_VERSION,
    generatedAt,
    observationCutoff,
    cohortStart,
    cohortEnd,
    collection: {
      startedAt: typeof startedAt === 'string' ? startedAt : null,
      finishedAt: typeof finishedAt === 'string' ? finishedAt : null,
      complete,
      provisional,
      stopReason: typeof stopReason === 'string' ? stopReason : null,
      sources: {
        trials: sources.trials,
        purchases: sources.purchases,
      },
    },
    coverage,
    headline: { matureTrials, converted, rate },
    cohorts,
    trialStartsByPlacement,
    limitations,
  };
  return { ok: true, report };
}
